import type { SimpleStyleT } from "tweenly"
import { MAX_INPUT_NUM } from "../../../constants"

// Line styles — the editor's copy of tweenly's stroke.styles.ts, keep the two in sync.
// A line is drawn by an SVG <line> inside its box; stroke-linecap and stroke-dasharray are
// inherited CSS properties, so rendering them on the box is enough. Render functions must
// stay self-contained: they are serialised with json-fn and revived with `new Function`, so
// a reference to anything outside them would be lost.

/**
 * Line thickness. Rendered as a custom property because two things read it: the box's
 * height (so the selection hugs the line) and the SVG stroke width — see LINE_SVG.
 */
export const lineWeight: SimpleStyleT = {
    property: "lineWeight",
    value: 4,
    unit: "px",
    input: {
        type: "number",
        label: "weight",
        props: {
            inputProps: {
                min: 0,
                max: MAX_INPUT_NUM,
                step: 0.5,
            },
        },
    },
    render: (value, unit) => ({ property: "--line-weight", value: value + (unit || "px") }),
}

/** What the ends of the line (and of every dash) look like: butt, round or square. */
export const strokeLinecap: SimpleStyleT = {
    property: "strokeLinecap",
    value: "butt",
    input: {
        type: "select",
        label: "caps",
        props: ["butt", "round", "square"],
    },
    render: (value, unit, type) =>
        type === "css"
            ? { property: "stroke-linecap", value: String(value) }
            : { property: "strokeLinecap", value: String(value) },
}

/**
 * Dash pattern as `[dash, gap]` in px, like Figma's dashPattern. A gap of 0 is a solid
 * line; a dash of 0 with round caps draws dots.
 */
export const strokeDasharray: SimpleStyleT = {
    property: "strokeDasharray",
    value: [0, 0],
    input: {
        type: "string",
        label: "dash pattern",
        props: "",
    },
    render: (value, unit, type) => {
        const dash = Number(value && value[0]) || 0
        const gap = Number(value && value[1]) || 0
        return {
            property: type === "css" ? "stroke-dasharray" : "strokeDasharray",
            value: gap > 0 ? dash + " " + gap : "none",
        }
    },
}
